import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Modal, Row, Tooltip, Typography } from "antd";

import {
  signOut,
  isDocsAdminUser,
  isUserDocumentUser,
} from "../../../modules/session";
import colors from "../../../colors";
import {
  put,
  get,
  deleteApi,
  checkHttpStatus,
} from "../../../modules/httpFunctions";
import Load from "../../app/generic/load";
import {
  parseDate,
  parseDateTime,
} from "../../../modules/consignmentFunctions";
import InfoLabel from "../../app/generic/infoLabel";
import StateIcon from "../../app/generic/stateIcon";
import LoadTable from "../../app/generic/loadTable";
import { saveBlob } from "../../../modules/saveFile";
import CustomIcon from "../../app/generic/customIcon";
import ExternalLink from "../../app/generic/externalLink";
import { handleError } from "../../../modules/errorLogger";
import { error, success, warn } from "../../../modules/messages";

const { confirm } = Modal;

const UserData = ({ operation }) =>
  operation ? (
    <>
      <Row>
        <Col>{parseDateTime(operation.dateTime)}</Col>
      </Row>
      <Row>
        <Col>{operation.email || operation.username}</Col>
      </Row>
    </>
  ) : (
    <></>
  );

export default function Table({ documents, processed, onLoadDocuments }) {
  const intl = useIntl();
  const close = intl.formatMessage({
    id: "messages.close",
    defaultMessage: "Zavřít",
  });

  const { user } = useSelector((state) => state.session);
  const userDoc = isUserDocumentUser(user?.role);
  const docsAdmin = isDocsAdminUser(user?.role);

  const data =
    documents && documents !== "e"
      ? documents.filter((d) =>
          processed
            ? d.confirmer || d.rejecter || d.autoResulter
            : !d.confirmer && !d.rejecter && !d.autoResulter
        )
      : documents;

  const [loading, setLoading] = useState();
  function handleDropClick(doc) {
    const onError = () =>
      error(
        intl.formatMessage({
          id: "document.drop.error",
          defaultMessage: "Při rušení dokumentu nastala chyba.",
        })
      );
    confirm({
      title: intl.formatMessage({
        id: "messages.confirm",
        defaultMessage: "Potvrzení",
      }),
      content: intl.formatMessage({
        id: "messages.drop.question",
        defaultMessage: "Opravdu si přejete odstranit tento záznam?",
      }),
      okText: intl.formatMessage({
        id: "messages.yes",
        defaultMessage: "Ano",
      }),
      cancelText: intl.formatMessage({
        id: "messages.no",
        defaultMessage: "Ne",
      }),
      onOk: () => {
        setLoading(true);
        deleteApi(`document/${doc.documentId}`)
          .then((r) => {
            if (checkHttpStatus(r)) {
              onLoadDocuments();
              success(
                intl.formatMessage({
                  id: "document.drop.success",
                  defaultMessage:
                    "Pro stávajícího klienta byl dokument úspěšně zrušen.",
                })
              );
            } else {
              onError();
            }
            setLoading();
          })
          .catch((err) => {
            handleError(err);
            setLoading();
            onError();
          });
      },
    });
  }

  function handleCloseClick(doc, reject) {
    const onError = () =>
      error(
        intl.formatMessage({
          id: "document.resolve.error",
          defaultMessage: "Při zpracování dokumentu nastala chyba.",
        })
      );
    setLoading(true);
    put(`document/${doc.documentId}/${reject ? "reject" : "confirm"}`)
      .then((r) => {
        if (checkHttpStatus(r)) {
          onLoadDocuments();
          success(
            intl.formatMessage({
              id: "document.resolve.success",
              defaultMessage: "Dokument byl úspěšně zpracován.",
            })
          );
          if (reject && doc.blockOnReject) {
            warn(
              intl.formatMessage({
                id: "document.resolve.logout",
                defaultMessage:
                  "Váš účet byl zablokován z důvodu zamítnutí důležitého dokumentu DHL.",
              })
            );
            signOut();
          }
        } else {
          onError();
        }
        setLoading();
      })
      .catch((err) => {
        handleError(err);
        setLoading();
        onError();
      });
  }

  function handleDownload(doc) {
    const onError = () =>
      error(
        intl.formatMessage({
          id: "document.file.error",
          defaultMessage:
            "Stažení souboru se nepovedlo, zkuste to prosím znovu.",
        })
      );
    get(`document/${doc.documentId}/file`, true, undefined, true)
      .then((r) => {
        if (checkHttpStatus(r)) {
          saveBlob(r.data, doc.fileName);
        } else {
          onError();
        }
      })
      .catch((err) => {
        handleError(err);
        onError();
      });
  }

  const columns = [
    ...(processed
      ? []
      : [
          {
            key: "blockOnReject",
            dataIndex: "blockOnReject",
            width: 60,
            className: "center",
            render: (blockOnReject) =>
              blockOnReject ? (
                <CustomIcon
                  size={27}
                  color={colors.RED}
                  type="exclamation-circle"
                  title={
                    <FormattedMessage
                      id="document.blockOnReject.true"
                      defaultMessage="Pokud odmítnete tento dokument, dojde k zablokování přístupu ke službám DHL Freight CZ."
                    />
                  }
                />
              ) : (
                <CustomIcon
                  type="info"
                  title={
                    <FormattedMessage
                      id="document.blockOnReject.false"
                      defaultMessage="Dokument je pouze informativního charakteru."
                    />
                  }
                />
              ),
          },
        ]),
    {
      key: "name",
      dataIndex: "name",
      title: (
        <FormattedMessage id="document.name.client" defaultMessage="Název" />
      ),
      sorter: (a, b) => (a.name ? a.name.localeCompare(b.name) : -1),
      render: (name, document) => (
        <Row type="flex" gutter={10} className="pointer">
          {document.link ? (
            <ExternalLink
              href={document.url}
              className="pointer"
              titleId="document.link.title"
              defaultMessage="Kliknutím zobrazíte dokument"
              content={<Typography.Text underline>{name}</Typography.Text>}
            />
          ) : (
            <Tooltip
              placement="left"
              title={
                <FormattedMessage
                  id="document.download"
                  defaultMessage="Kliknutím stáhnete dokumentu."
                />
              }
            >
              <Col onClick={() => handleDownload(document)}>
                <Typography.Text underline>{name}</Typography.Text>
              </Col>
            </Tooltip>
          )}
          {document.description ? (
            <Col>
              <CustomIcon
                type="wechat"
                onClick={() =>
                  Modal.info({
                    content: document.description,
                    okText: close,
                  })
                }
                title={
                  <FormattedMessage
                    id="document.description.info"
                    defaultMessage="Kliknutím zobrazíte popis dokumentu."
                  />
                }
              />
            </Col>
          ) : (
            <></>
          )}
        </Row>
      ),
    },
    {
      key: "documentType",
      dataIndex: "documentType",
      title: (
        <FormattedMessage id="document.type" defaultMessage="Typ dokumentu" />
      ),
      sorter: (a, b) =>
        a.documentType ? a.documentType.localeCompare(b.documentType) : -1,
      render: (documentType) => (
        <FormattedMessage
          id={`document.${documentType}`}
          defaultMessage="Neznámý"
        />
      ),
    },
    {
      key: "inserter",
      dataIndex: "inserter",
      title: (
        <FormattedMessage id="document.inserter" defaultMessage="Vložil" />
      ),
      render: (inserter) => <UserData operation={inserter} />,
    },
    {
      key: "validFrom",
      dataIndex: "validFrom",
      title: (
        <FormattedMessage id="document.validFrom" defaultMessage="Platné od" />
      ),
      render: (validFrom) => parseDate(validFrom),
      sorter: (a, b) =>
        a.validFrom ? a.validFrom.localeCompare(b.validFrom) : -1,
    },
    ...(processed
      ? [
          {
            key: "confirmer",
            dataIndex: "confirmer",
            title: (
              <FormattedMessage id="document.result" defaultMessage="Odpověď" />
            ),
            render: (confirmer, { rejecter, autoResulter, autoResult }) => {
              const confirm = confirmer || (autoResult === 1 && autoResulter);
              return (
                <Row type="flex" gutter={10} align="middle">
                  <Col>
                    <StateIcon
                      valid={confirm ? true : false}
                      messages={[
                        <FormattedMessage
                          id={
                            confirm ? "document.rejected" : "document.confirmed"
                          }
                          defaultMessage={
                            confirm
                              ? "Dokument byl schválen."
                              : "Dokument byl odmítnut."
                          }
                        />,
                      ]}
                    />
                  </Col>
                  <Col>
                    <UserData
                      operation={autoResulter ?? confirmer ?? rejecter}
                    />
                  </Col>
                </Row>
              );
            },
          },
        ]
      : [
          {
            key: "validTo",
            dataIndex: "validTo",
            title: (
              <FormattedMessage
                id="document.validTo"
                defaultMessage="Vyjádření do"
              />
            ),
            sorter: (a, b) =>
              a.validTo ? a.validTo.localeCompare(b.validTo) : -1,
            render: (validTo, { autoResult }) =>
              validTo ? (
                <InfoLabel
                  label={parseDate(validTo)}
                  info={
                    autoResult === 2 ? (
                      <FormattedMessage
                        id="document.validTo.reject"
                        defaultMessage="Po tomto datu bude dokument automaticky zamítnut."
                      />
                    ) : (
                      <FormattedMessage
                        id="document.validTo.approve"
                        defaultMessage="Po tomto datu bude dokument automaticky schválen."
                      />
                    )
                  }
                />
              ) : (
                "--"
              ),
          },
          {
            key: "functions",
            dataIndex: "functions",
            render: (_, document) =>
              loading ? (
                <Load noMessage />
              ) : (
                <Row type="flex" gutter={20}>
                  <Col>
                    <CustomIcon
                      type="check"
                      color={colors.GREEN}
                      className={userDoc ? "pointer" : ""}
                      onClick={
                        userDoc ? () => handleCloseClick(document) : undefined
                      }
                      title={
                        userDoc ? (
                          <FormattedMessage
                            id="document.confirm"
                            defaultMessage="Kliknutím akceptujete dokument"
                          />
                        ) : (
                          <FormattedMessage
                            id="document.confirm.role"
                            defaultMessage="Ve stávající roli nemáte možnost zpracovat dokument"
                          />
                        )
                      }
                    />
                  </Col>
                  <Col>
                    <CustomIcon
                      type="close"
                      color={colors.RED}
                      className={userDoc ? "pointer" : ""}
                      onClick={
                        userDoc
                          ? () => handleCloseClick(document, true)
                          : undefined
                      }
                      title={
                        userDoc ? (
                          <FormattedMessage
                            id="document.reject"
                            defaultMessage="Kliknutím odmítnete dokument"
                          />
                        ) : (
                          <FormattedMessage
                            id="document.confirm.role"
                            defaultMessage="Ve stávající roli nemáte možnost zpracovat dokument"
                          />
                        )
                      }
                    />
                  </Col>
                  {docsAdmin && (
                    <Col>
                      <CustomIcon
                        type="delete"
                        className="pointer"
                        onClick={() => handleDropClick(document)}
                        title={
                          <FormattedMessage
                            id="document.drop"
                            defaultMessage="Zrušit přiřazení dokumentu pro tohoto klienta"
                          />
                        }
                      />
                    </Col>
                  )}
                </Row>
              ),
          },
        ]),
  ];

  return (
    <Col span={24}>
      <Typography.Title level={3} className="title-dhl">
        <FormattedMessage
          id={processed ? "document.processed" : "document.notProcessed"}
          defaultMessage={
            processed ? "Zpracované dokumenty" : "Dokumenty ke zpracování"
          }
        />
      </Typography.Title>
      <LoadTable
        pageSize={5}
        values={data}
        columns={columns}
        className="no-pointer"
        onLoadValues={onLoadDocuments}
        emptyText={
          <FormattedMessage
            id={
              processed
                ? "document.processed.noData"
                : "document.notProcessed.noData"
            }
            defaultMessage={
              processed
                ? "Nemáte žádné dokumenty"
                : "Nemáte žádné dokumenty ke zpracování"
            }
          />
        }
        what={
          <FormattedMessage id="document.what" defaultMessage="dokumentů" />
        }
      />
    </Col>
  );
}
